/**
 * Helper to safely access import.meta in environments that support it
 * Returns undefined in test environments where import.meta is not available
 */
function getImportMetaEnv(): Record<string, any> | undefined {
  try {
    // Vitest supports import.meta natively, so we can access it directly
    return import.meta?.env;
  } catch {
    return undefined;
  }
}

/**
 * Get environment variable with support for:
 * 1. Docker runtime injection (window.__RUNTIME_CONFIG__) - highest priority in production
 * 2. Vite (import.meta.env.VITE_*)
 * 3. process.env (for NODE_ENV, etc.)
 * 4. Default fallback value
 *
 * @param name - Variable name WITHOUT prefix (e.g., "ENV", not "VITE_ENV")
 * @param defaultValue - Optional default value if not found
 * @returns Environment variable value or undefined
 */
export function getEnvVar(name: string, defaultValue?: string): string | undefined {
  // 1. Try Docker runtime config first (highest priority for production)
  if (window.__RUNTIME_CONFIG__ && window.__RUNTIME_CONFIG__[name] !== undefined) {
    return window.__RUNTIME_CONFIG__[name];
  }

  // 2. Try Vite environment variables (modern)
  const viteEnv = getImportMetaEnv();
  if (viteEnv) {
    const viteValue = viteEnv[`VITE_${name}`];
    if (viteValue !== undefined) {
      return viteValue;
    }
  }

  // 3. Try process.env (for NODE_ENV, etc.)
  if (typeof process !== 'undefined' && process.env) {
    const plainValue = process.env[name];
    if (plainValue !== undefined) {
      return plainValue;
    }
  }

  // 4. Return default value
  return defaultValue;
}
